import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { NAV_HEIGHT_PX, BOTTOM_SAFE_AREA } from '@/constants/nav';

/**
 * Floating "back to top" pill. Appears after the user scrolls past
 * ~1.5× viewport height and smooth-scrolls back when tapped.
 *
 * Sits above the mobile bottom-nav (offset bumped on small screens) and
 * respects the iOS safe area. Hidden by default until enough content has
 * been scrolled so it never clutters short pages.
 */
export default function ScrollToTopPill() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // With <body> as the actual scroll container, `window.scrollY` no longer
    // updates — read `document.body.scrollTop` (and fall back to scrollY for
    // older browsers / desktop where the html may still scroll).
    const getScrollTop = () =>
      document.body.scrollTop || document.documentElement.scrollTop || window.scrollY || 0;
    const scrollEl = document.body;
    const onScroll = () => {
      const threshold = window.innerHeight * 1.5;
      setVisible(getScrollTop() > threshold);
    };
    onScroll();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleClick = () => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          data-testid="scroll-to-top-pill"
          aria-label="Scroll back to top"
          onClick={handleClick}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="fixed right-4 z-[60] inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#101F1D]/90 backdrop-blur-xl border border-white/15 text-white text-xs font-semibold shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:bg-[#162039] hover:border-white/25 transition-colors"
          style={{
            // Sit 16 px above the bottom-nav row, plus the home-indicator
            // inset so the pill clears Apple's chrome on iPhones with a
            // home bar.  Keep in step with `NAV_HEIGHT_PX`.
            bottom: `calc(${BOTTOM_SAFE_AREA} + ${NAV_HEIGHT_PX + 16}px)`,
          }}
        >
          <ArrowUp className="w-4 h-4" />
          <span>Top</span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
